import { PageContextType } from '../../types';

/**
 * getConfluenceContentAri function from confluence/next/packages/content-types-utils/src/contentTypeConstants.ts
 */

export const TYPES_WITH_ACTIVATIONID: string[] = ['database', 'embed', 'folder'];

type ConfluenceContentAriParameters = {
	siteId: string;
	resourceType: string;
	activationId: string;
	resourceId: string;
};

/**
 * Builds a registered ARI from the ARI registry. go/ari to learn more
 */
export const getConfluenceContentAri = (
	confluenceContentAriParameters: ConfluenceContentAriParameters,
): string => {
	const { siteId, resourceType, activationId, resourceId } = confluenceContentAriParameters;
	if (TYPES_WITH_ACTIVATIONID.includes(resourceType)) {
		return `ari:cloud:confluence:${siteId}:${resourceType}/activation/${activationId}/${resourceId}`;
	}

	return `ari:cloud:confluence:${siteId}:${resourceType}/${resourceId}`;
};

/**
 * Extracts resourceId from Confluence URLs (pages and blogs).
 * - Page Edit: /wiki/spaces/{spaceKey}/pages/edit-v2/{resourceId}
 * - Page View: /wiki/spaces/{spaceKey}/pages/{resourceId}/{title}
 * - Blog Edit: /wiki/spaces/{spaceKey}/blog/edit-v2/{resourceId}
 * - Blog View: /wiki/spaces/{spaceKey}/blog/{year}/{month}/{day}/{resourceId}/{title}
 */

export const extractResourceIdFromUrl = (
	url: string,
	pageContextType: PageContextType,
): string | null => {
	if (
		pageContextType === PageContextType.CONFLUENCE_PAGE_EDIT ||
		pageContextType === PageContextType.CONFLUENCE_BLOG_EDIT
	) {
		// Match: /pages/edit-v2/{resourceId} or /blog/edit-v2/{resourceId}
		const match = url.match(/\/(?:pages|blog)\/edit-v2\/(\d+)/);
		return match ? match[1] : null;
	}

	if (pageContextType === PageContextType.CONFLUENCE_PAGE_VIEW) {
		// Page pattern: /pages/{resourceId}/
		const match = url.match(/\/pages\/(\d+)(?:\/|$|[?#])/);
		return match ? match[1] : null;
	}

	if (pageContextType === PageContextType.CONFLUENCE_BLOG_VIEW) {
		// Blog pattern: /blog/{year}/{month}/{day}/{resourceId}/
		const match = url.match(/\/blog\/\d{4}\/\d{2}\/\d{2}\/(\d+)/);
		return match ? match[1] : null;
	}

	return null;
};

export const getContentAri = (
	siteId: string,
	contentUrl: string,
	pageContextType: PageContextType,
): string | null => {
	const resourceId = extractResourceIdFromUrl(contentUrl, pageContextType);
	if (!resourceId) {
		return null;
	}

	const isBlogPost = /\/spaces\/[^\/]+\/blog\//.test(contentUrl);
	const resourceType = isBlogPost ? 'blogpost' : 'page';

	return getConfluenceContentAri({
		siteId,
		resourceType,
		activationId: '', // Not needed for pages/blogposts
		resourceId,
	});
};
